import { useState, useCallback, useEffect } from 'react';
import { string, func, bool, arrayOf, shape } from 'prop-types';
import { debounce } from 'debounce';
import { ENTER, ESC } from '../../../utils/constants/key_codes';

const SearchInput = ({ query, loading, results, handleSearchResultClick, placeholderText, className, updateQuery, inputRef, onFocus, handleExit, disabled, readOnly }) => {
  const [shouldTrack, updateShouldTrack] = useState(true);
  const [currentQuery, updateCurrentQuery] = useState(query);
  const [isWaitingToQuery, updateIsWaitingToQuery] = useState(false);
  const [shouldNavigateToFirstResult, updateShouldNavigateToFirstResult] = useState(false);

  const handleKeyUp = e => {
    e.preventDefault();

    if (e.keyCode === ESC) {
      handleExit();
    }
  };

  const handleKeyDown = e => {
    if (e.keyCode === ENTER && isWaitingToQuery) {
      e.preventDefault();
      updateShouldNavigateToFirstResult(true);
    } else {
      updateShouldNavigateToFirstResult(false);
    }
  }

  useEffect(() => {
    if (shouldNavigateToFirstResult && !loading && !isWaitingToQuery && results.length > 0) {
      handleSearchResultClick(results[0])
    }
  }, [shouldNavigateToFirstResult, isWaitingToQuery, loading]);

  const doSearch = useCallback(
    debounce(q => {
      updateQuery(q);
      updateIsWaitingToQuery(false);
    }, 250),
    []
  );

  const handleOnChange = e => {
    const q = e.currentTarget.value;

    updateIsWaitingToQuery(true);
    updateCurrentQuery(q);
    doSearch(q);

    if (shouldTrack) {
      updateShouldTrack(false);
    }
  };

  return (
    <input
      ref={inputRef}
      value={currentQuery}
      disabled={disabled}
      onFocus={onFocus}
      aria-label={placeholderText}
      autoComplete="off"
      autoCorrect="off"
      data-testid="algoliaPlaceholderText"
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onChange={handleOnChange}
      type="text"
      className={className}
      placeholder={placeholderText}
      spellCheck="false"
      readOnly={readOnly}
    />
  );
};

SearchInput.propTypes = {
  query: string,
  toggleEngaged: func,
  placeholderText: string,
  className: string,
  updateQuery: func,
  disabled: bool,
  readOnly: bool,
  loading: bool,
  results: arrayOf(
    shape({
      name: string,
      location: string,
      objectID: string,
      url: string,
      type: string
    })
  ),
  handleSearchResultClick: func
};

SearchInput.defaultProps = {
  query: string,
  toggleEngaged: () => {},
  placeholderText: '',
  className: '',
  updateQuery: () => {},
  disabled: false,
  readOnly: false,
  loading: false,
  results: [],
  handleSearchResultClick: () => {}
};

export default SearchInput;
